import * as signalR from "@microsoft/signalr";
import { getApiKey } from "./apiKeyProvider";
import { BACKEND_URL } from "./aiapi";
import { getAiCenterFlags } from "./aiCenterFlags";


/**
 * 연결 1개를 모듈 스코프에서 유지·재사용합니다.
 * 연결 인증(Hub 레벨 [Authorize])은 accessTokenFactory로 처리하고
 * (WebSocket에서는 쿼리스트링 access_token, HTTP 기반 요청에서는 Authorization 헤더로 자동 전달),
 * JoinJobGroup의 tenantId 판별은 메서드 인자로 apiKey를 직접 넘겨서 처리합니다.
 */
let connectionPromise:any = null;
const jobListeners = new Map(); // jobId -> { onComplete, onError, onQueued, onMessage }
let tenantListener:any = null;   // job 존재 여부와 무관하게 tenant 전체 customMessage를 받는 단일 리스너
const isAiOn = () => getAiCenterFlags()?.aiCenter === true; // ai center 설정이 on 되어 있어야 signalr Connection이 실행 되도록 변경 tikim - 261022


function getConnection() {
  if (!connectionPromise) {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${BACKEND_URL}/hubs/emailjob`, {
        accessTokenFactory: () => getApiKey(),
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on("jobComplete", (jobId) => {
      console.log("[signalr] jobComplete", jobId);
      jobListeners.get(jobId)?.onComplete?.();
    });

    connection.on("jobError", (jobId, message) => {
      console.log("[signalr] jobError", jobId, message);
      jobListeners.get(jobId)?.onError?.(message);
    });

    connection.on("jobQueued", (jobId) => {
      console.log("[signalr] jobQueued", jobId);
      jobListeners.get(jobId)?.onQueued?.();
    });

    connection.on("customMessage", (message) => {
      console.log("[signalr] customMessage", message);
      jobListeners.forEach((listener) => listener.onMessage?.(message));
      tenantListener?.onMessage?.(message);
    });

    connection.onreconnecting((error) => console.warn("[signalr] reconnecting...", error));
    connection.onreconnected(() => console.log("[signalr] reconnected"));
    connection.onclose((error) => console.warn("[signalr] connection closed", error));

    connectionPromise = connection
      .start()
      .then(() => {
        console.log("[signalr] connected");
        return connection;
      })
      .catch((err) => {
        connectionPromise = null; // 실패 시 다음 시도에서 재연결하도록 초기화
        throw err;
      });
  }
  return connectionPromise;
}

/**
 * SignalR 연결이 활성화될 때까지 대기합니다.
 * Disconnected 상태면 재연결을 시도하고, Connecting/Reconnecting 상태면 Connected될 때까지 대기합니다.
 */
async function ensureConnected(connection: signalR.HubConnection): Promise<void> {
  // 이미 연결된 상태
  if (connection.state === signalR.HubConnectionState.Connected) {
    return;
  }

  // 완전히 끊어진 상태 → 재연결 시도
  if (connection.state === signalR.HubConnectionState.Disconnected) {
    try {
      await connection.start();
      return;
    } catch (error) {
      throw new Error(`Failed to start SignalR connection: ${error}`);
    }
  }

  // Connecting 또는 Reconnecting 상태 → Connected 될 때까지 대기
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('SignalR connection timeout (10s)'));
    }, 10000);

    const checkInterval = setInterval(() => {
      if (connection.state === signalR.HubConnectionState.Connected) {
        clearInterval(checkInterval);
        clearTimeout(timeout);
        resolve();
      } else if (connection.state === signalR.HubConnectionState.Disconnected) {
        clearInterval(checkInterval);
        clearTimeout(timeout);
        reject(new Error('Connection disconnected while waiting'));
      }
    }, 100);
  });
}

export async function subscribeToJob(jobId:string, listeners:any) {
  if (!isAiOn()) throw new Error("AI Center is turned off.");
  const connection = await getConnection();
  jobListeners.set(jobId, listeners);
  try {
    await ensureConnected(connection);
    await connection.invoke("JoinJobGroup", jobId, getApiKey());
  } catch(error) {
    console.error("Realtime conneciton failed:", error);
    throw error;
  }
}

export function unsubscribeFromJob(jobId:string) {
  jobListeners.delete(jobId);
}

// job 존재 검증 없이 tenant 그룹에 join한다 (백엔드 EmailJobHub.JoinTenantGroup 필요)
export async function subscribeToTenant(listeners:any) {
   if (!isAiOn()) return; 
  const connection = await getConnection();
  tenantListener = listeners;
  try {
    await ensureConnected(connection);
    await connection.invoke("JoinTenantGroup", getApiKey());
  } catch(error) {
    console.error("Realtime connection failed:", error);
    throw error;
  }
}

export function unsubscribeFromTenant() {
  tenantListener = null;
}


export async function stopConnection() {
  if (!connectionPromise) return;
  const p = connectionPromise;
  connectionPromise = null;
  jobListeners.clear();
  tenantListener = null;
  try {
    const connection = await p;
    await connection.stop();
  } catch {
  }
}